// navLights.ts - the small lights on an authored hull: running lights, strobes,
// work lamps and the habitat's windows.
//
// One THREE.Points per ship and one draw call: every light is a point sprite
// carrying its own colour, size, kind and phase, sized in metres so it shrinks
// with distance like the hull does, with a floor of a pixel or two so a ship
// at the game's chase distance still shows its lights. Emission only - no
// THREE.Light, nothing added to the lighting pass.
//
//   RUNNING   steady red at the port wing tip, green at starboard (port is -X:
//             facing forward, -Z, with +Y dorsal).
//   STROBE    white double flash at the bow, the stern and the dorsal peak,
//             on a slow 2.6 s cadence, each ship on its own phase.
//   WORK      a few dim amber lamps over the boarding hatches and the
//             engineering collar.
//   WINDOWS   dim warm strips along both flanks of the habitat section,
//             52-78 m aft of the bow on the 148 m Custos (canon §VII D),
//             scaled to the hull's own length.
//
// Everything is placed from the hull manifest's measured profile and module
// stations (tools/parts/hull_profile.mjs), in the frame the engine draws.

import * as THREE from 'three';
import type { HullManifest } from './shipSpec';

export const NAV_LIGHTS = {
  /** seconds between strobe bursts, flash length, gap inside a double flash */
  strobePeriodS: 2.6,
  strobeFlashS: 0.07,
  strobeGapS: 0.28,
  /** habitat stations as fractions of the length aft of the bow (canon 52-78 m of 148) */
  habitatFrom: 52 / 148,
  habitatTo: 78 / 148,
  /** the smallest a light draws, in pixels */
  minPixels: 1.6,
} as const;

export type LightKind = 'running' | 'strobe' | 'work' | 'window';
const KIND_CODE: Record<LightKind, number> = { running: 0, strobe: 1, work: 2, window: 3 };

export interface NavLight {
  kind: LightKind;
  at: [number, number, number];
  /** linear rgb, pre-multiplied by intensity; over 1 blooms */
  color: [number, number, number];
  /** sprite diameter in ship units */
  size: number;
}

export interface NavLights {
  readonly object: THREE.Points | null;
  readonly lights: readonly NavLight[];
  /** the lamp's output at time t (s), 0 or 1 for a strobe, 1 for the steady kinds */
  level(i: number, t: number): number;
  update(t: number): void;
  /** Move the running lights onto the drawn wing tips once the hull's mesh has landed (ship frame). */
  setRunning(port: THREE.Vector3, starboard: THREE.Vector3): void;
  dispose(): void;
}

// ---- the control ----
export type NavLightsInjection = 'no-lights' | null;
let injected: NavLightsInjection = null;
/** `no-lights` builds none. Set by tools/preview/nav-lights-check.mjs --inject. */
export function setNavLightsInjected(mode: NavLightsInjection): void {
  injected = mode;
}

/** 1 while a strobe is lit: a double flash each period, offset by `phase` seconds. */
export function strobeLevel(t: number, phase: number): number {
  const p = NAV_LIGHTS.strobePeriodS;
  const w = (((t + phase) % p) + p) % p;
  const f = NAV_LIGHTS.strobeFlashS, g = NAV_LIGHTS.strobeGapS;
  return w < f || (w >= g && w < g + f) ? 1 : 0;
}

const bin = (m: HullManifest, z: number, arr: readonly number[]): number => {
  const p = m.profile;
  const i = Math.min(Math.max(Math.floor((z - p.z0) / p.pitch), 0), arr.length - 1);
  return arr[i] ?? 0;
};

/** Where an authored hull's lights go, from its manifest alone. */
export function placeNavLights(m: HullManifest): NavLight[] {
  const p = m.profile;
  const out: NavLight[] = [];
  const [zBow, zStern] = m.zRange;
  const len = zStern - zBow;
  const n = p.hw.length;
  const zOf = (i: number): number => p.z0 + (i + 0.5) * p.pitch;

  // running lights at the widest station: the wing tips
  let iw = 0;
  for (let i = 1; i < n; i++) if ((p.hw[i] ?? 0) > (p.hw[iw] ?? 0)) iw = i;
  const tip = (p.hw[iw] ?? 0) + 0.02;
  out.push({ kind: 'running', at: [-tip, 0, zOf(iw)], color: [3.2, 0.12, 0.08], size: 0.09 });
  out.push({ kind: 'running', at: [tip, 0, zOf(iw)], color: [0.12, 2.8, 0.5], size: 0.09 });

  // strobes: the first and last occupied stations, and the dorsal peak
  let i0 = 0, i1 = n - 1, ih = 0;
  while (i0 < n - 1 && !((p.r[i0] ?? 0) > 0)) i0++;
  while (i1 > 0 && !((p.r[i1] ?? 0) > 0)) i1--;
  for (let i = 0; i < n; i++) if ((p.hh[i] ?? 0) > (p.hh[ih] ?? 0)) ih = i;
  const white: [number, number, number] = [4, 4, 4.2];
  out.push({ kind: 'strobe', at: [0, (p.hh[i0] ?? 0) * 0.5, zOf(i0) - p.pitch * 0.5], color: white, size: 0.08 });
  out.push({ kind: 'strobe', at: [0, (p.hh[i1] ?? 0) + 0.03, zOf(i1)], color: white, size: 0.08 });
  if (ih !== i0 && ih !== i1) out.push({ kind: 'strobe', at: [0, (p.hh[ih] ?? 0) + 0.03, zOf(ih)], color: white, size: 0.07 });

  // work lamps over each boarding hatch and at the engineering collar, both flanks
  const amber: [number, number, number] = [1.3, 0.62, 0.14];
  for (const mod of m.modules) {
    if (mod.kind !== 'boardingBay' && mod.kind !== 'collar') continue;
    const r = bin(m, mod.z, p.rBody) + 0.03;
    for (const sx of [-1, 1]) out.push({ kind: 'work', at: [sx * r * 0.94, r * 0.34, mod.z], color: amber, size: 0.05 });
  }

  // habitat windows: a row a little above the waterline on each flank
  const zA = zBow + NAV_LIGHTS.habitatFrom * len, zB = zBow + NAV_LIGHTS.habitatTo * len;
  const count = Math.max(4, Math.round((zB - zA) / 0.16));
  const warm: [number, number, number] = [0.55, 0.45, 0.3];
  const th = 0.33;
  for (let k = 0; k < count; k++) {
    const z = zA + (k + 0.5) * (zB - zA) / count;
    const r = bin(m, z, p.rBody) + 0.02;
    // a window here and there dark: people, not a strip light
    if (((k * 7919) % 11) === 3) continue;
    for (const sx of [-1, 1]) out.push({ kind: 'window', at: [sx * r * Math.cos(th), r * Math.sin(th), z], color: warm, size: 0.035 });
  }
  return out;
}

const VS = /* glsl */ `
attribute vec3 aColor;
attribute vec3 aInfo; // size, kind, phase
uniform float uTime;
uniform float uPxPerUnit;
uniform float uMinPx;
uniform vec3 uStrobe; // period, flash, gap
varying vec3 vColor;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  float level = 1.0;
  if (aInfo.y > 0.5 && aInfo.y < 1.5) {
    float w = mod(uTime + aInfo.z, uStrobe.x);
    level = (w < uStrobe.y || (w >= uStrobe.z && w < uStrobe.z + uStrobe.y)) ? 1.0 : 0.0;
  }
  float px = aInfo.x * uPxPerUnit / max(-mv.z, 1e-3);
  // below the floor a light keeps its pixel and gives up brightness instead
  float fade = clamp(px / uMinPx, 0.0, 1.0);
  vColor = aColor * level * fade * fade;
  gl_PointSize = level > 0.0 ? max(px, uMinPx) : 0.0;
  gl_Position = projectionMatrix * mv;
}`;
const FS = /* glsl */ `
varying vec3 vColor;
void main() {
  vec2 d = gl_PointCoord - 0.5;
  float a = exp(-dot(d, d) * 18.0);
  if (a < 0.02) discard;
  gl_FragColor = vec4(vColor * a, 1.0);
}`;

/** The lights for one authored hull, parented by the caller to the ship's visual group. */
export function createNavLights(manifest: HullManifest, seed = 0): NavLights {
  const lights = injected === 'no-lights' ? [] : placeNavLights(manifest);
  const phase = (seed * 0.618034) % 1 * NAV_LIGHTS.strobePeriodS;
  const phases = lights.map((l, i) => (l.kind === 'strobe' ? phase + i * 0.9 : 0));
  let object: THREE.Points | null = null;
  let geo: THREE.BufferGeometry | null = null;
  let mat: THREE.ShaderMaterial | null = null;
  if (lights.length) {
    geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(lights.flatMap((l) => l.at), 3));
    geo.setAttribute('aColor', new THREE.Float32BufferAttribute(lights.flatMap((l) => l.color), 3));
    geo.setAttribute('aInfo', new THREE.Float32BufferAttribute(lights.flatMap((l, i) => [l.size, KIND_CODE[l.kind], phases[i]!]), 3));
    mat = new THREE.ShaderMaterial({
      vertexShader: VS, fragmentShader: FS,
      uniforms: {
        uTime: { value: 0 }, uPxPerUnit: { value: 500 }, uMinPx: { value: NAV_LIGHTS.minPixels },
        uStrobe: { value: new THREE.Vector3(NAV_LIGHTS.strobePeriodS, NAV_LIGHTS.strobeFlashS, NAV_LIGHTS.strobeGapS) },
      },
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    object = new THREE.Points(geo, mat);
    object.name = 'nav-lights';
    object.frustumCulled = false;
    const size = new THREE.Vector2();
    const sc = new THREE.Vector3(), tp = new THREE.Vector3(), tq = new THREE.Quaternion();
    const u = mat.uniforms;
    // pixels per ship unit at unit depth, with the ship's own world scale folded in
    object.onBeforeRender = (renderer, _scene, camera) => {
      renderer.getDrawingBufferSize(size);
      const proj = (camera as THREE.PerspectiveCamera).projectionMatrix.elements[5] ?? 1;
      object!.matrixWorld.decompose(tp, tq, sc);
      u.uPxPerUnit!.value = proj * size.y * 0.5 * sc.x;
    };
  }
  return {
    object,
    lights,
    level(i: number, t: number): number {
      const l = lights[i];
      if (!l) return 0;
      return l.kind === 'strobe' ? strobeLevel(t, phases[i]!) : 1;
    },
    update(t: number): void {
      if (mat) mat.uniforms.uTime!.value = t;
    },
    setRunning(port: THREE.Vector3, starboard: THREE.Vector3): void {
      const pos = geo?.getAttribute('position') as THREE.BufferAttribute | undefined;
      lights.forEach((l, i) => {
        if (l.kind !== 'running') return;
        const v = l.at[0] < 0 ? port : starboard;
        l.at = [v.x, v.y, v.z];
        pos?.setXYZ(i, v.x, v.y, v.z);
      });
      if (pos) pos.needsUpdate = true;
    },
    dispose(): void {
      object?.removeFromParent();
      geo?.dispose();
      mat?.dispose();
    },
  };
}
